import { useState } from "react";
import * as XLSX from "xlsx";
import { FileSpreadsheet, Download, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const PS = /^\s*(\d+)\s*[.,]\s*(\d+)\s*$/;

const HEAD = { server: "device", port: "port", nomor: "number", no: "number", "no hp": "number", operator: "name", provider: "name", "nama sim": "name", pt: "company" };

function parseRows(table) {
  const rows = table.filter((r) => r && r.some((c) => String(c ?? "").trim()));
  const head = rows.length && !PS.test(String(rows[0][1] ?? "")) ? rows[0].map((c) => HEAD[String(c ?? "").trim().toLowerCase()]) : null;
  const wide = rows.some((r) => {
    let n = r.length;
    while (n && !String(r[n - 1] ?? "").trim()) n--;
    return n >= 5;
  });
  const cols = head && head.filter(Boolean).length >= 3 ? head : wide ? ["device", "port", "number", "name", "company"] : ["device", "port", "name", "company"];
  return rows.slice(head ? 1 : 0).map((r) => {
    const v = {};
    cols.forEach((k, i) => k && (v[k] = String(r[i] ?? "").trim()));
    const m = (v.port || "").match(PS);
    return { device: v.device || "", port: m ? Number(m[1]) : 0, slot: m ? Number(m[2]) : 0, raw: v.port || "",
      name: v.name || "", company: v.company || "", number: v.number || "" };
  });
}

function template() {
  const ws = XLSX.utils.aoa_to_sheet([["Server", "Port", "Nomor", "Operator", "PT"], ["ej2", "1.7", "081216132149", "tsel1", "BP"], ["ej2", "2.7", "081216132150", "tsel2", "BP"]]);
  ws["!cols"] = [{ wch: 10 }, { wch: 8 }, { wch: 16 }, { wch: 14 }, { wch: 8 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Nama SIM");
  XLSX.writeFile(wb, "template-nama-sim.xlsx");
}

export function BulkNameDialog({ onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const fromText = (t) => { setText(t); setRes(null); setRows(parseRows(t.split(/\r?\n/).map((l) => l.split(/\t|;/)))); };
  const fromFile = async (f) => {
    if (!f) return;
    const wb = XLSX.read(await f.arrayBuffer());
    const table = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false, defval: "" });
    setRes(null);
    setRows(parseRows(table));
  };
  const run = async (dry) => {
    setBusy(true);
    try {
      const r = await api.post("/slots/bulk", { rows: rows.map(({ raw, ...x }) => x), dry_run: dry });
      setRes(r.data);
      if (!dry) {
        toast.success(`${r.data.ok} dari ${r.data.total} slot diberi nama`);
        onSaved();
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const status = (i) => res?.rows?.[i];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bulk-name-dialog" className="bg-white border-slate-300 max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-green-600" />Nama massal dari Excel</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Kolom: <b>Server</b> (ej1/ej2/sk) · <b>Port</b> (mis. 1.7) · <b>Nomor</b> (nomor SIM) · <b>Operator</b> (nama SIM, mis. tsel1) · <b>PT</b> (mis. BP). Upload file .xlsx/.csv atau salin dari Excel lalu tempel di bawah.</p>
        <p className="text-xs text-amber-700">Tips: format kolom Port sebagai <b>Text</b> di Excel, supaya 1.10 tidak berubah jadi 1.1. Format lama tanpa Nomor (4 kolom) tetap bisa. Sel Nomor/Operator/PT yang kosong tidak mengubah data yang sudah ada.</p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
            <FileSpreadsheet className="h-4 w-4" />Pilih file Excel
            <input data-testid="bulk-file-input" type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => fromFile(e.target.files?.[0])} />
          </label>
          <Button data-testid="bulk-template" variant="ghost" size="sm" onClick={template}><Download className="h-4 w-4 mr-1" />Unduh template</Button>
        </div>
        <Textarea data-testid="bulk-paste" rows={4} placeholder={"ej2\t1.7\t081216132149\ttsel1\tBP\nej2\t2.7\t081216132150\ttsel2\tBP"} value={text} onChange={(e) => fromText(e.target.value)} className="font-mono text-xs bg-white" />
        {rows.length > 0 && (
          <div className="max-h-64 overflow-auto rounded border border-slate-200" data-testid="bulk-preview">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 sticky top-0"><tr><th className="p-1.5 text-left">#</th><th className="text-left">Server</th><th className="text-left">Port</th><th className="text-left">Nomor</th><th className="text-left">Nama SIM</th><th className="text-left">PT</th><th className="text-left">Status</th></tr></thead>
              <tbody>
                {rows.map((r, i) => {
                  const st = status(i);
                  return (
                    <tr key={i} data-testid={`bulk-row-${i + 1}`} className="border-t border-slate-100">
                      <td className="p-1.5 text-slate-400">{i + 1}</td><td>{r.device}</td>
                      <td className={`font-mono ${r.port ? "" : "text-red-600"}`}>{r.port ? `${r.port}.${r.slot}` : r.raw || "?"}</td>
                      <td className="font-mono text-xs">{r.number}</td>
                      <td className="font-semibold">{r.name}</td><td>{r.company}</td>
                      <td className="text-xs">{st ? (st.ok ? <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle2 className="h-3.5 w-3.5" />OK</span> : <span className="inline-flex items-center gap-1 text-red-600"><XCircle className="h-3.5 w-3.5" />{st.error}</span>) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span data-testid="bulk-summary" className="text-xs text-slate-500 mr-auto">{rows.length ? `${rows.length} baris` : "Belum ada data"}{res ? ` · ${res.ok} siap/berhasil` : ""}</span>
          <Button data-testid="bulk-check" variant="outline" size="sm" disabled={!rows.length || busy} onClick={() => run(true)}>Cek dulu</Button>
          <Button data-testid="bulk-apply" size="sm" disabled={!rows.length || busy} onClick={() => run(false)} className="bg-emerald-500 hover:bg-emerald-600 text-white">
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Simpan semua
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
